import axios from 'axios';
import { computed } from 'vue';
import router from '../router';
import { useUserStore } from '../stores/user';
import { useMiscStore } from '../stores/store';
import { emitter } from '@/core/emitter';
import SentryUtils from '@/core/SentryUtils';
import { useAuthStore } from '@/stores/auth';

// Key for the core-api idempotency middleware (Idempotency-Key header). The
// server replays the first response for a repeated key instead of executing
// again, so a duplicated money-moving POST (double-submit, transport retry)
// can't place/edit twice.
export function generateIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useAPI(url, ignoreError = false, options = {}) {
  // event bus and store values
  const userStore = useUserStore();
  const miscStore = useMiscStore();
  const authStore = useAuthStore();

  const MAX_RETRIES = 1;
  const RETRY_DELAY = 1000; // 0.5 second

  // Per-request timeout override. The instance default (120s) is sized for
  // heavy affiliate queries; fast endpoints should fail fast instead.
  const requestTimeout = options.timeout;

  // Create a single Axios instance for reuse
  const apiClient = axios.create({
    baseURL: import.meta.env.VITE_APP_API_ENDPOINT,
    timeout: 120000, // 2 minutes for heavy affiliate queries
    responseType: 'json',
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // set Auth Headers
  const setAuthorizationHeaders = () => ({
    authorization: authStore.token,
    'x-user-socket-status': miscStore.socketStatus.userFeedSocket,
    'x-price-socket-status': miscStore.socketStatus.priceUpdateSocket,
  });

  const requiredAuth = computed(() => {
    return router.currentRoute?.meta?.requiresAuth || false;
  });

  const request = async (
    request,
    controller = null,
    { idempotencyKey } = {},
  ) => {
    SentryUtils.addBreadcrumb(
      'api',
      `Starting POST request to ${url}`,
      'info',
      { request },
    );
    try {
      const isLoggedIn = !!userStore.user?.isLoggedIn;
      if (!requiredAuth.value || isLoggedIn) {
        const response = await apiClient.post(url, request, {
          headers: {
            ...setAuthorizationHeaders(),
            ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
          },
          signal: controller?.signal || undefined,
        });
        SentryUtils.addBreadcrumb(
          'api',
          `Successfully completed request to ${url}`,
        );
        return handleResponse(response);
      }
    } catch (error) {
      SentryUtils.addBreadcrumb(
        'api',
        `Failed POST request to ${url}`,
        'error',
        { error: error.message },
      );
      if (ignoreError) return;
      return handleError(error, url, request);
    }
  };

  const retryGet = async (params, controller, retries = 0) => {
    try {
      const isLoggedIn = !!userStore.user?.isLoggedIn;
      if (!requiredAuth.value || isLoggedIn) {
        const response = await apiClient.get(url, {
          params: params,
          headers: setAuthorizationHeaders(),
          signal: controller?.signal || undefined,
          ...(requestTimeout ? { timeout: requestTimeout } : {}),
        });
        SentryUtils.addBreadcrumb('api', `Successful GET request to ${url}`);
        return handleResponse(response);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      // A timeout already waited the full window; retrying just doubles the
      // wait before the failure surfaces, so don't retry on ECONNABORTED.
      const isTimeout = error.code === 'ECONNABORTED';
      if (retries < MAX_RETRIES && !isTimeout) {
        SentryUtils.addBreadcrumb(
          'api',
          `Retrying GET request to ${url}. Attempt ${retries + 1}`,
          'info',
        );
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return retryGet(params, controller, retries + 1);
      }
      SentryUtils.addBreadcrumb(
        'api',
        `Failed GET request to ${url}`,
        'error',
        { error: error.message },
      );
      return handleError(error, url, params);
    }
  };

  const get = async (params, controller = null) => {
    SentryUtils.addBreadcrumb('api', `Starting GET request to ${url}`, 'info', {
      params,
    });
    return retryGet(params, controller);
  };

  const handleResponse = (response) => {
    if (response && response.headers && response.headers['x-auth-token']) {
      emitter.emit('updateAuthToken', response.headers['x-auth-token']);
    }

    if (response.data && response.data.error) {
      SentryUtils.addBreadcrumb('api', 'API returned an error', 'warning', {
        error: response.data.error,
      });
      throw new Error(response.data.error);
    }
    return response.data.data;
  };

  const handleError = (error, url, requestData) => {
    let message;
    if (error.response?.data?.error?.message) {
      message = error.response.data.error.message;
    } else if (error.message) {
      message = error.message;
    } else {
      message = 'An unexpected error occurred';
    }

    const errorData = error?.response?.data?.error ?? {};
    // Context to distinguish a backend hang from a degraded/suspended client
    // connection (the usual culprit on mobile) without guessing.
    const visibilityState =
      typeof document !== 'undefined' ? document.visibilityState : 'unknown';
    const connectionType =
      typeof navigator !== 'undefined' && navigator.connection
        ? navigator.connection.effectiveType
        : 'unknown';
    SentryUtils.captureException(error, {
      tags: {
        endpoint: url,
        failure_code: error.code || 'unknown',
        visibility_state: visibilityState,
      },
      extra: {
        message: message,
        url: url,
        requestData: requestData,
        socketStatus: { ...miscStore.socketStatus },
        errorData,
        errorCode: error.code,
        connectionType,
        visibilityState,
        httpStatus: error.response?.status,
      },
    });
    if (message === 'Network Error' && !url.includes('/getMe')) {
      SentryUtils.addBreadcrumb(
        'api',
        `Emitting network error route ${url} error ${JSON.parse(JSON.stringify(error))}`,
      );
      emitter.emit('NetworkError', error);
    } else if (error) {
      const errorMessage = error.response?.data?.error?.message;
      // Only force a logout when the user is actually logged in.
      // AccountClosed can also come back from public endpoints
      // (e.g. /user/sendPasswordRevokeEmail) where the caller has
      // their own UI handling and we shouldn't reset the app state.
      const isLoggedIn = !!userStore.user?.isLoggedIn;
      if (
        isLoggedIn &&
        (errorMessage === 'InvalidCredentials' ||
          errorMessage === 'AccountClosed')
      ) {
        emitter.emit('userLogOut');
      } else if (
        error.response?.status === 500 &&
        !url.includes('/getTopTraded') &&
        !url.includes('/getUpcomingEvent')
      ) {
        emitter.emit('ApplicationError');
      }
    }
    const thrown = new Error(message);
    // Carry the server's machine-readable error contract through — callers
    // like the login challenge flow key on details.code, never the message.
    thrown.status = error.response?.status;
    thrown.details = error.response?.data?.error?.details;
    throw thrown;
  };

  return { request, get };
}
